import { CardColors, CardFooter, CardFooterItem } from "$components/card";
import { FormattedDate } from "$components/date/formatted-date";
import {
  type Disease,
  RXDB_DISEASE_COLLECTION_NAME,
} from "$global-providers/rxdb-provider/collections/disease";
import useFacility from "$routes/_lib/use-facility";
import {
  CardHeader,
  PatientHistoryCard,
} from "$routes/patient/_lib/history-card";
import { FormattedMessage } from "react-intl";
import { useRxData } from "rxdb-hooks";
import { getDiseaseCode } from "../vaccinations/_lib/get-disease-code";
import { useHistoryUserDetailsQuery } from "./__generated__/user-details.gql.gql";
import { allVaccineCodesToDetailsMap } from "./vaccinations-service/vaccination-schedule";

export type VaccinationConsultation = {
  uuid: string;
  meta: { created: string; createdByUserId: string };
  diseases?: readonly { id: string }[];
  backdatedTo?: string;
  isPerformedAtOtherClinic?: boolean;
};

export const VaccinationHistoryCard: React.FC<{
  consultation: VaccinationConsultation;
}> = ({ consultation }) => {
  const { data: facility } = useFacility();
  const { data: consultationUser } = useHistoryUserDetailsQuery({
    variables: { userId: consultation.meta.createdByUserId },
  });

  const { result } = useRxData<Disease>(
    RXDB_DISEASE_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          id: {
            $in: consultation.diseases?.map((disease) => disease.id) ?? [],
          },
        },
      }),
  );
  const immunisationDiseases = result.map((disease) => {
    const code = getDiseaseCode(disease);
    return allVaccineCodesToDetailsMap[code].displayName;
  });

  if (immunisationDiseases.length === 0) {
    return null;
  }

  return (
    <PatientHistoryCard
      color={CardColors.PRESCRIPTION}
      hideExpandIcon
      HeaderComponent={() => (
        <CardHeader
          color={CardColors.PRESCRIPTION}
          category="Vaccination"
          title={immunisationDiseases.join(", ")}
          id={consultation.uuid}
        />
      )}
      FooterComponent={() => {
        const userName = consultationUser?.user?.person?.firstname
          ? `${consultationUser.user.person.firstname} ${
              consultationUser.user.person.lastname ?? ""
            }`.trim()
          : null;
        const atDifferentLocation =
          consultation.isPerformedAtOtherClinic ||
          consultationUser?.user?.clinic !== facility?.id;
        const by = userName && (
          <CardFooterItem
            label={
              <FormattedMessage
                id="vaccination-history-card.footer.deliveredBy"
                defaultMessage="By:"
              />
            }
          >
            {userName}
          </CardFooterItem>
        );
        return (
          <CardFooter>
            <CardFooterItem
              label={
                <FormattedMessage
                  id="vaccination-history-card.footer.deliveredOn"
                  defaultMessage="Delivered:"
                />
              }
            >
              <FormattedDate
                value={consultation.backdatedTo ?? consultation.meta.created}
              />
            </CardFooterItem>
            {consultation.backdatedTo ? (
              <CardFooterItem
                label={
                  <FormattedMessage
                    id="vaccination-history-card.footer.deliveredAt"
                    defaultMessage="At:"
                  />
                }
              >
                {atDifferentLocation ? (
                  <FormattedMessage
                    id="vaccination-history-card.footer.deliveredAt.differentLocation"
                    defaultMessage="A different location"
                  />
                ) : (
                  <FormattedMessage
                    id="vaccination-history-card.footer.deliveredAt.sameFacility"
                    defaultMessage="This facility"
                  />
                )}
              </CardFooterItem>
            ) : (
              by
            )}
            {consultation.backdatedTo && (
              <>
                <CardFooterItem
                  label={
                    <FormattedMessage
                      id="vaccination-history-card.footer.recordedOn"
                      defaultMessage="Recorded:"
                    />
                  }
                >
                  <FormattedDate value={consultation.meta.created} />
                </CardFooterItem>
                {by}
              </>
            )}
          </CardFooter>
        );
      }}
    >
      {null}
    </PatientHistoryCard>
  );
};
